import { useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCancelBooking } from '../hooks/useBookings';
import { is400, is404 } from '../utils/errors';

interface CancelBookingDialogProps {
  readonly bookingId: string;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export const CancelBookingDialog = ({
  bookingId,
  isOpen,
  onClose,
  onSuccess,
}: CancelBookingDialogProps) => {
  const { t } = useTranslation(['common', 'pages', 'errors', 'forms', 'badges', 'navigation']);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const cancelMutation = useCancelBooking();

  if (!isOpen) return null;

  const handleClose = () => {
    setReason('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      await cancelMutation.mutateAsync({
        id: bookingId,
        data: { cancellationReason: reason },
      });
      setReason('');
      setError(null);
      onSuccess();
    } catch (err) {
      if (is404(err)) {
        setError(t('errors:bookingNotFound'));
      } else if (is400(err)) {
        setError(t('errors:cancellationInvalid'));
      } else {
        setError(t('errors:somethingWentWrong'));
      }
    }
  };

  const isPending = cancelMutation.isPending;
  const isDisabled = reason.trim().length === 0 || isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">{t('pages:cancelBookingHeading')}</h2>
          <button
            onClick={handleClose}
            disabled={isPending}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            aria-label={t('common:dismiss')}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            {t('pages:cancelBookingBody')}
          </p>

          <div>
            <label
              htmlFor="cancellation-reason"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t('forms:cancellationReason')} <span className="text-red-500">{t('common:required')}</span>
            </label>
            <textarea
              id="cancellation-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              disabled={isPending}
              maxLength={500}
              rows={4}
              placeholder={t('forms:cancellationReasonPlaceholder')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50 disabled:cursor-not-allowed resize-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">
              {t('common:charCounter', { current: reason.length, max: 500 })}
            </p>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <button
            onClick={handleClose}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('common:dismiss')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isDisabled}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending && (
              <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {t('pages:confirmCancellation')}
          </button>
        </div>
      </div>
    </div>
  );
};
